import { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  makeStyles
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import MultiSelectTable from "src/components/MultiSelectTable";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";

import { FormAnswer } from "./types";

const useStyles = makeStyles(() => ({
  content: {
    width: "80vw",
    maxWidth: "600px"
  }
}));

export default function VANCanvassResultImport({
  open,
  onClose,
  addAnswer,
  campaign,
  maxAnswers
}: {
  open: boolean;
  onClose: () => void;
  addAnswer: (answer: FormAnswer) => void;
  campaign: getCampaign_campaign;
  maxAnswers: number;
}): JSX.Element | null {
  const classes = useStyles();

  const [selectedCanvassResults, setSelectedCanvassResults] = useState<
    Set<number>
  >(new Set());

  const isValid = selectedCanvassResults.size <= maxAnswers;

  const canvassResultOptions =
    campaign.vanIntegration?.canvassResultCodes ?? [];

  function handleClose() {
    setSelectedCanvassResults(new Set());
    onClose();
  }

  function handleSubmit() {
    selectedCanvassResults.forEach(vanCanvassResultId => {
      const canvassResult = canvassResultOptions.find(
        opt => opt.resultCodeId === vanCanvassResultId
      );

      const answer: FormAnswer = {
        __typename: "CampaignAnswer",
        key: uuidv4(), // placeholder, filled in when we send to the server
        value: canvassResult?.name ?? "",
        next: null,
        disposition: null,
        vanIntegrationSettings: {
          __typename: "CampaignAnswerVanIntegrationSettings",
          activistCodeId: null,
          activistCodeData: null,
          surveyResponseId: null,
          resultCodeId: vanCanvassResultId
        },
        patchThruConfig: null
      };

      addAnswer(answer);
    });

    handleClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="van-canvass-result-dialog-title"
      aria-describedby="van-canvass-result-dialog-description"
    >
      <DialogTitle id="van-canvass-result-dialog-title">
        Import NGP VAN Canvass Results
      </DialogTitle>
      <DialogContent className={classes.content}>
        <DialogContentText id="van-canvass-result-dialog-description">
          <p>Select which NGP VAN Canvass Results to use.</p>
          <MultiSelectTable
            data={canvassResultOptions}
            columns={[
              {
                header: "Canvass Result",
                key: "name",
                render: (name: string) => name
              }
            ]}
            containerStyle={{ maxHeight: 500 }}
            onChangeSelectedRows={setSelectedCanvassResults}
            selectedRows={selectedCanvassResults}
            primaryKey="resultCodeId"
          />

          {!isValid && (
            <Alert severity="error">
              You can only add {maxAnswers} answers for each question.
            </Alert>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!isValid}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
